<template>
  <div id="events-bidding-manual-entry" class="page-container">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular
        indeterminate
        size="64"
        class="navy-text"
      ></v-progress-circular>
    </v-overlay>

    <!-- Abstract JCI themed glowing background elements -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- Premium Page Header -->
    <v-card
      elevation="8"
      class="header-card mb-8 overflow-hidden animate-slide-down"
    >
      <div class="header-gradient">
        <!-- Floating JCI themed particles in background -->
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>

        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" lg="8">
              <div class="d-flex align-center flex-wrap flex-sm-nowrap">
                <div class="header-icon-box glow-effect">
                  <v-icon size="48" color="white">mdi-gavel</v-icon>
                </div>
                <div class="ml-sm-6 mt-4 mt-sm-0 text-left">
                  <h1 class="header-title white--text mb-2 animate-pulse-slow">
                    Event Bidding Manual Entry
                  </h1>
                  <p class="header-subtitle white--text mb-0">
                    Enterprise Portal for Administrative Event Proposal
                    Ingestion & Hierarchy Alignment
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" lg="4" class="text-lg-right mt-4 mt-lg-0">
              <v-btn
                to="/events-bidding/list"
                color="white"
                light
                rounded
                large
                class="action-btn navy-text font-weight-bold px-6 py-3"
                elevation="4"
              >
                <v-icon left size="20">mdi-arrow-left</v-icon>
                Back to List
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Separate Proposal Summary Card -->
    <v-card
      elevation="6"
      class="preview-dashboard-card mb-8 overflow-hidden animate-slide-down delay-1"
    >
      <div
        class="preview-dashboard-header py-4 px-6 d-flex align-center border-bottom-light"
      >
        <v-icon class="navy-text mr-2">mdi-view-dashboard-outline</v-icon>
        <span class="preview-dashboard-title font-weight-bold off-black-text"
          >Live Proposal Summary</span
        >
      </div>
      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <div class="summary-box summary-navy animate-zoom-in delay-1">
              <div class="summary-icon bg-navy-light">
                <v-icon class="navy-text">mdi-star</v-icon>
              </div>
              <div class="summary-text-box">
                <div class="summary-label">Event Selected</div>
                <div class="summary-value off-black-text">
                  {{
                    (EventTypeOptions.find((o) => o.value === EventName) || {})
                      .text || "Not Selected"
                  }}
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="summary-box summary-aqua animate-zoom-in delay-2">
              <div class="summary-icon bg-aqua-light">
                <v-icon class="aqua-text">mdi-map-marker-distance</v-icon>
              </div>
              <div class="summary-text-box">
                <div class="summary-label">Zone</div>
                <div class="summary-value off-black-text">
                  {{
                    (ZoneCodeOptions.find((o) => o.value === ZoneCode) || {})
                      .text || "Not Selected"
                  }}
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="summary-box summary-seaform animate-zoom-in delay-3">
              <div class="summary-icon bg-seaform-light">
                <v-icon class="seaform-text">mdi-domain</v-icon>
              </div>
              <div class="summary-text-box">
                <div class="summary-label">LO (Chapter)</div>
                <div class="summary-value off-black-text">
                  {{
                    (LomOptions.find((o) => o.value === Lom) || {}).text ||
                    "Not Selected"
                  }}
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="summary-box summary-gold animate-zoom-in delay-4">
              <div class="summary-icon bg-gold-light">
                <v-icon class="gold-text">mdi-shield-check</v-icon>
              </div>
              <div class="summary-text-box">
                <div class="summary-label">Proposal Status</div>
                <div class="summary-value status-badge gold-text">
                  <span class="pulse-indicator"></span>
                  Pending Submit
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-form
      ref="formManual"
      v-model="validManual"
      lazy-validation
      class="animate-fade-in position-relative"
    >
      <div class="animate-staggered">
        <!-- Section 1: Event Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-navy"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-navy-light navy-text mr-4">01</div>
            <div class="panel-icon mr-4">
              <v-icon large class="navy-text">mdi-calendar-star</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">Event Information</h2>
              <span class="panel-subtitle black-90-text"
                >Select the target program/event type</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!EventName }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Event Name
                </div>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="EventTypeOptionsLoading"
                  :items="EventTypeOptions"
                  :rules="[(v) => !!v || 'Event Name is required']"
                  v-model="EventName"
                  dense
                  outlined
                  placeholder="Select event..."
                  prepend-inner-icon="mdi-calendar-star"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 2: Zone & LO Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-aqua"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-aqua-light aqua-text mr-4">02</div>
            <div class="panel-icon mr-4">
              <v-icon large class="aqua-text">mdi-map-clock-outline</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">Zone & LO Information</h2>
              <span class="panel-subtitle black-90-text"
                >Assign the bidding Zone and LOM (LO)</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!ZoneCode && !!Lom }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12" md="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Zone
                </div>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="ZoneCodeOptionsLoading"
                  :items="ZoneCodeOptions"
                  :rules="[(v) => !!v || 'Zone is required']"
                  v-model="ZoneCode"
                  @change="getLomOptions"
                  dense
                  outlined
                  placeholder="Select Zone..."
                  prepend-inner-icon="mdi-map-marker-distance"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> LO
                </div>
                <v-autocomplete
                  v-model="Lom"
                  :reduce="(option) => option.value"
                  :items="LomOptions"
                  :loading="LomOptionsLoading"
                  :rules="[(v) => !!v || 'Chapter / LO is required']"
                  dense
                  outlined
                  placeholder="Search and select Chapter / LO..."
                  prepend-inner-icon="mdi-domain"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 3: Venue Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-gold"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-gold-light gold-text mr-4">03</div>
            <div class="panel-icon mr-4">
              <v-icon large class="gold-text">mdi-town-hall</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">Venue Information</h2>
              <span class="panel-subtitle black-90-text"
                >Define proposed schedule, capacity, and travel distances</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!FromDate && !!ToDate && !!StageType }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Proposed From Date
                </div>
                <v-menu
                  v-model="menuFromDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="FromDate"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      placeholder="Select start date..."
                      :rules="[(v) => !!v || 'From date is required']"
                      class="premium-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="FromDate"
                    @input="menuFromDate = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Proposed To Date
                </div>
                <v-menu
                  v-model="menuToDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="ToDate"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      placeholder="Select end date..."
                      :rules="[(v) => !!v || 'To date is required']"
                      class="premium-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="ToDate"
                    @input="menuToDate = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Stage Type
                </div>
                <v-select
                  v-model="StageType"
                  :items="StageTypeOptions"
                  :rules="[(v) => !!v || 'Stage Type is required']"
                  outlined
                  dense
                  placeholder="Select Stage Type..."
                  prepend-inner-icon="mdi-human-stage-play"
                  class="premium-input"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Proposed Venue <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="Venue"
                  outlined
                  dense
                  placeholder="Enter venue name..."
                  prepend-inner-icon="mdi-office-building-marker"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Proposed Venue Type
                  <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="VenueType"
                  outlined
                  dense
                  placeholder="e.g. Hotel, Auditorium..."
                  prepend-inner-icon="mdi-office-building"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Hall Capacity <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="HallCapacity"
                  outlined
                  dense
                  placeholder="e.g. 500 Pax..."
                  prepend-inner-icon="mdi-account-group"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Available Rooms <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="AvailableRooms"
                  outlined
                  dense
                  placeholder="e.g. 50 Rooms..."
                  prepend-inner-icon="mdi-bed-double"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Distance from Airport (Km)
                  <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="AirportDistance"
                  outlined
                  dense
                  placeholder="e.g. 15..."
                  prepend-inner-icon="mdi-airplane"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Distance from Railway Stn (Km)
                  <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="TrainDistance"
                  outlined
                  dense
                  placeholder="e.g. 5..."
                  prepend-inner-icon="mdi-train"
                  class="premium-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  Distance from Bus Stn (Km)
                  <small class="text-muted">(optional)</small>
                </div>
                <v-text-field
                  v-model="BusDistance"
                  outlined
                  dense
                  placeholder="e.g. 3..."
                  prepend-inner-icon="mdi-bus"
                  class="premium-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 4: Location Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-seaform"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-seaform-light seaform-text mr-4">04</div>
            <div class="panel-icon mr-4">
              <v-icon large class="seaform-text">mdi-map-marker-radius</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">Location Information</h2>
              <span class="panel-subtitle black-90-text"
                >Select State, District, and City</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!StateId && !!DistrictId && !!CityName }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> State
                </div>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="StateIdOptionsLoading"
                  :items="StateIdOptions"
                  :rules="[(v) => !!v || 'State is required']"
                  v-model="StateId"
                  @change="onStateChange"
                  outlined
                  dense
                  placeholder="Select State..."
                  prepend-inner-icon="mdi-map"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> District
                </div>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="DistrictIdOptionsLoading"
                  :items="DistrictIdOptions"
                  :rules="[(v) => !!v || 'District is required']"
                  v-model="DistrictId"
                  :disabled="!StateId"
                  @change="onDistrictChange"
                  outlined
                  dense
                  placeholder="Select District..."
                  prepend-inner-icon="mdi-map-marker"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> City
                </div>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="CityIdOptionsLoading"
                  :items="CityIdOptions"
                  :rules="[(v) => !!v || 'City is required']"
                  v-model="CityName"
                  :disabled="!DistrictId"
                  outlined
                  dense
                  placeholder="Select City..."
                  prepend-inner-icon="mdi-city"
                  class="premium-input"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 5: LGB Resolution Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-navy"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-navy-light navy-text mr-4">05</div>
            <div class="panel-icon mr-4">
              <v-icon large class="navy-text">mdi-file-document-outline</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">
                LGB Resolution Information
              </h2>
              <span class="panel-subtitle black-90-text"
                >Upload LGB meeting documents and enter remarks</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!LgbHeldDate && !!Remarks }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Date of LO LGB
                </div>
                <v-menu
                  v-model="menuLgbDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="LgbHeldDate"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      placeholder="Select LGB date..."
                      :rules="[(v) => !!v || 'LGB date is required']"
                      class="premium-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="LgbHeldDate"
                    @input="menuLgbDate = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="8" sm="6">
                <div class="field-label black-90-text">
                  Attach LGB Resolution
                  <small class="text-muted">(optional)</small>
                </div>
                <v-file-input
                  v-model="UploadedFile1"
                  accept="image/png, image/jpeg, image/bmp, application/pdf"
                  label="Choose Resolution File"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  class="premium-input"
                ></v-file-input>
              </v-col>

              <v-col cols="12">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Remarks
                </div>
                <v-textarea
                  v-model="Remarks"
                  :rules="[(v) => !!v || 'Remarks are required']"
                  outlined
                  dense
                  rows="3"
                  placeholder="Enter additional remarks or LGB decisions..."
                  prepend-inner-icon="mdi-comment-text-outline"
                  class="premium-input"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 6: Organizing Committee Information -->
        <v-card class="premium-section-card mb-6 overflow-hidden">
          <div class="card-indicator bg-aqua"></div>
          <div class="card-header-content d-flex align-center pa-6">
            <div class="badge-num bg-aqua-light aqua-text mr-4">06</div>
            <div class="panel-icon mr-4">
              <v-icon large class="aqua-text">mdi-account-multiple</v-icon>
            </div>
            <div class="text-left">
              <h2 class="panel-title off-black-text">
                Organizing Committee Information
              </h2>
              <span class="panel-subtitle black-90-text"
                >Define local organizing committee structure and details</span
              >
            </div>
            <v-spacer></v-spacer>
            <div
              class="section-progress-dot"
              :class="{ completed: !!OrganizingCommitteeDetails }"
            ></div>
          </div>
          <v-card-text class="px-6 pb-6 pt-2">
            <v-row>
              <v-col cols="12">
                <div class="field-label black-90-text">
                  <span class="text-danger">*</span> Organizing Committee
                  Details
                </div>
                <v-textarea
                  v-model="OrganizingCommitteeDetails"
                  :rules="[
                    (v) => !!v || 'Organizing Committee Details are required',
                  ]"
                  outlined
                  dense
                  rows="4"
                  placeholder="Enter organizing committee members and roles..."
                  prepend-inner-icon="mdi-account-details"
                  class="premium-input"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>

      <!-- Submit Section -->
      <v-row class="mt-8 mb-12 animate-fade-in justify-center">
        <v-col cols="12" md="8" class="text-center">
          <v-btn
            :disabled="!validManual"
            @click.prevent="confirmManualSubmit"
            :loading="SubmitFlag"
            color="primary"
            elevation="12"
            rounded
            x-large
            class="submit-btn px-12 py-8 font-weight-bold animate-pulse"
          >
            <v-icon left size="28">mdi-checkbox-marked-circle-outline</v-icon>
            Submit Enterprise Proposal
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  data() {
    return {
      validManual: true,
      OverlayLoadingFlag: false,
      SubmitFlag: false,

      // Options Loaders
      EventTypeOptionsLoading: false,
      EventTypeOptions: [],
      EventName: "",

      // Zone options
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      // Chapter / LOM options
      Lom: "",
      LomOptions: [],
      LomOptionsLoading: false,

      // Dates
      FromDate: new Date().toISOString().slice(0, 10),
      ToDate: new Date().toISOString().slice(0, 10),
      LgbHeldDate: new Date().toISOString().slice(0, 10),
      menuFromDate: false,
      menuToDate: false,
      menuLgbDate: false,

      // Stage & Venue
      StageTypeOptions: [
        { value: 1, text: "Theatre Style" },
        { value: 2, text: "U-Shape-Style" },
        { value: 3, text: "Round-Table" },
      ],
      StageType: "",
      Venue: "",
      VenueType: "",
      HallCapacity: "",
      AvailableRooms: "",
      AirportDistance: "",
      TrainDistance: "",
      BusDistance: "",

      // States & Districts
      StateId: "",
      StateIdOptions: [],
      StateIdOptionsLoading: false,

      DistrictId: "",
      DistrictIdOptions: [],
      DistrictIdOptionsLoading: false,

      CityName: "",
      CityIdOptions: [],
      CityIdOptionsLoading: false,

      // Resolution File & Remarks
      UploadedFile1: null,
      Remarks: "",
      OrganizingCommitteeDetails: "",

      PageInfo: {},
      JciYearCode: "",
    };
  },
  mounted() {
    this.refreshPageData();
  },
  watch: {
    PageInfo: function () {
      if (this.PageInfo && Object.keys(this.PageInfo).length > 0) {
        // if (!this.PageInfo.PagePermissionFlag) {
        //   this.$router.push("/access-denied");
        // }
        this.JciYearCode = parseInt(this.PageInfo.CurrentJciYearId);
        this.getEventTypeOptions();
        this.getZoneCodeOptions();
        this.getStateIdOptions();
      }
    },
    ZoneCodeOptions: function () {
      this.ZoneCodeOptionsLoading = false;
    },
    LomOptions: function () {
      this.LomOptionsLoading = false;
    },
    EventTypeOptions: function () {
      this.EventTypeOptionsLoading = false;
    },
    StateIdOptions: function () {
      this.StateIdOptionsLoading = false;
    },
    DistrictIdOptions: function () {
      this.DistrictIdOptionsLoading = false;
    },
    CityIdOptions: function () {
      this.CityIdOptionsLoading = false;
    },
  },
  methods: {
    refreshPageData() {
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "events_bidding",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getEventTypeOptions() {
      this.EventTypeOptionsLoading = true;
      var selectbox1_source = "EventType";
      var selectbox1_destination = "EventTypeOptions";
      var selectbox1_url = "api/jci-program-type/options";
      var selectbox1_conditions_array = { UserInterface: 2 };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      var yearCode = this.JciYearCode;
      if (yearCode != "") {
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomOptions() {
      this.Lom = "";
      this.LomOptions = [];
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      if (YearCode != "" && ZoneCode != "") {
        this.LomOptionsLoading = true;
        var selectbox1_source = "Lom";
        var selectbox1_destination = "LomOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getStateIdOptions() {
      this.StateIdOptionsLoading = true;
      var selectbox1_source = "StateId";
      var selectbox1_destination = "StateIdOptions";
      var selectbox1_url = "api/states/options";
      var selectbox1_conditions_array = { UserInterface: 1 };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    onStateChange() {
      this.DistrictId = "";
      this.CityName = "";
      this.DistrictIdOptions = [];
      this.CityIdOptions = [];
      if (this.StateId) {
        this.DistrictIdOptionsLoading = true;
        var selectbox1_source = "DistrictId";
        var selectbox1_destination = "DistrictIdOptions";
        var selectbox1_url = "api/district/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          StatesCode: this.StateId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    onDistrictChange() {
      this.CityName = "";
      this.CityIdOptions = [];
      if (this.DistrictId) {
        this.CityIdOptionsLoading = true;
        var selectbox1_source = "CityId";
        var selectbox1_destination = "CityIdOptions";
        var selectbox1_url = "api/city/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          DistrictCode: this.DistrictId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },

    confirmManualSubmit() {
      if (this.$refs.formManual.validate()) {
        Swal.fire({
          title: "Are you sure?",
          text: "Confirm all details before submitting manual entry.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Submit`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.submitManualForm();
          }
        });
      } else {
        this.toast("error", "Please correct the errors in the form.");
      }
    },
    submitManualForm() {
      let formData = new FormData();
      formData.append("UserInterface", 1);
      formData.append("EventName", this.EventName);
      formData.append("Lom", this.Lom);
      formData.append("FromDate", this.FromDate);
      formData.append("ToDate", this.ToDate);
      formData.append("StageType", this.StageType);
      formData.append("Venue", this.Venue);
      formData.append("VenueType", this.VenueType);
      formData.append("HallCapacity", this.HallCapacity);
      formData.append("AvailableRooms", this.AvailableRooms);
      formData.append("AirportDistance", this.AirportDistance);
      formData.append("TrainDistance", this.TrainDistance);
      formData.append("BusDistance", this.BusDistance);
      formData.append("CityName", this.CityName);
      formData.append("LgbHeldDate", this.LgbHeldDate);
      formData.append("Remarks", this.Remarks);
      formData.append("OrganizingCommittee", this.OrganizingCommitteeDetails);

      if (this.UploadedFile1) {
        formData.append("UploadedFile1", this.UploadedFile1);
      }

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/events/bidding/manual-store";

      this.SubmitFlag = true;
      this.OverlayLoadingFlag = true;

      const thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.SubmitFlag = false;
          thisIns.OverlayLoadingFlag = false;
          const flag = response.data.flag;
          const output = response.data.output;

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.$router.push("/events-bidding/list");
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.error(error);
          thisIns.SubmitFlag = false;
          thisIns.OverlayLoadingFlag = false;
          thisIns.sweetAlert(
            "error",
            "Submission failed. Please try again.",
            false
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
// JCI Approved Color Palette Constants
$jci-navy: #3a67b1;
$jci-aqua: #0097d7;
$jci-gold: #edbe38;
$jci-seaform: #56bda3;

// Text Colors
$jci-90-black: #414042;
$jci-off-black: #0a0f29;
$jci-white: #ffffff;

$card-shadow: 0 15px 35px rgba(10, 15, 41, 0.06);

// Typography Import (Outfit & Inter)
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@600;700;800;900&display=swap");

// Page Container
.page-container {
  font-family: "Inter", sans-serif;
  padding: 2.5rem;
  background: radial-gradient(
    circle at 50% 0%,
    rgba(58, 103, 177, 0.04) 0%,
    #f4f6fa 100%
  );
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

// Background Orbs using JCI colors
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(140px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.4;
  animation: floatOrb 25s infinite alternate ease-in-out;
}

.bg-orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(58, 103, 177, 0.15) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  top: -100px;
  right: -100px;
}

.bg-orb-2 {
  width: 600px;
  height: 600px;
  background: radial-gradient(
    circle,
    rgba(0, 151, 215, 0.12) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  bottom: -200px;
  left: -200px;
  animation-delay: -5s;
}

@keyframes floatOrb {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, -50px) scale(1.05);
  }
  100% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

// Header Card
.header-card {
  border-radius: 24px !important;
  box-shadow: $card-shadow !important;
  border: 1px solid rgba(58, 103, 177, 0.15) !important;
  z-index: 1;
  position: relative;
}

.header-gradient {
  background: linear-gradient(135deg, $jci-navy 0%, $jci-aqua 100%);
  position: relative;
  overflow: hidden;
}

// Particles in Header
.particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  pointer-events: none;
  animation: floatParticle 8s infinite ease-in-out;
}

.particle-1 {
  width: 10px;
  height: 10px;
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}
.particle-2 {
  width: 15px;
  height: 15px;
  top: 45%;
  left: 80%;
  animation-delay: 2s;
}
.particle-3 {
  width: 8px;
  height: 8px;
  top: 80%;
  left: 35%;
  animation-delay: 4s;
}
.particle-4 {
  width: 12px;
  height: 12px;
  top: 70%;
  left: 90%;
  animation-delay: 1s;
}

@keyframes floatParticle {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.15;
  }
  50% {
    transform: translateY(-15px) rotate(180deg);
    opacity: 0.4;
  }
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.glow-effect {
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
    opacity: 0.8;
    z-index: -1;
  }
}

.header-title {
  font-family: "Outfit", sans-serif;
  font-weight: 800;
  font-size: 2.5rem;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.header-subtitle {
  font-family: "Inter", sans-serif;
  font-size: 1.1rem;
  opacity: 0.9;
  font-weight: 400;
}

// Live Proposal Summary Dashboard Card
.preview-dashboard-card {
  background: $jci-white !important;
  border-radius: 22px !important;
  border: 1px solid rgba(10, 15, 41, 0.08) !important;
  border-top: 4px solid $jci-navy !important;
  box-shadow: $card-shadow !important;
  z-index: 1;
  position: relative;
}

.preview-dashboard-header {
  border-bottom: 1px solid rgba(10, 15, 41, 0.06);
}

.preview-dashboard-title {
  font-family: "Outfit", sans-serif;
  font-size: 1.25rem;
  letter-spacing: -0.2px;
}

.summary-box {
  background: $jci-white;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(10, 15, 41, 0.04);
  }
}

.summary-navy {
  border: 1px solid rgba(58, 103, 177, 0.15) !important;
  &:hover {
    border-color: rgba(58, 103, 177, 0.4) !important;
  }
}

.summary-aqua {
  border: 1px solid rgba(0, 151, 215, 0.15) !important;
  &:hover {
    border-color: rgba(0, 151, 215, 0.4) !important;
  }
}

.summary-seaform {
  border: 1px solid rgba(86, 189, 163, 0.15) !important;
  &:hover {
    border-color: rgba(86, 189, 163, 0.4) !important;
  }
}

.summary-gold {
  border: 1px solid rgba(237, 190, 56, 0.15) !important;
  &:hover {
    border-color: rgba(237, 190, 56, 0.4) !important;
  }
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.summary-text-box {
  text-align: left;
  overflow: hidden;
  width: 100%;
}

.summary-label {
  font-family: "Inter", sans-serif;
  font-size: 0.85rem;
  color: $jci-90-black;
  font-weight: 500;
  margin-bottom: 2px;
}

.summary-value {
  font-family: "Outfit", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Status badge
.status-badge {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
}

.pulse-indicator {
  width: 8px;
  height: 8px;
  background: $jci-gold;
  border-radius: 50%;
  margin-right: 8px;
  display: inline-block;
  box-shadow: 0 0 10px $jci-gold;
  animation: pulseGold 1.8s infinite;
}

@keyframes pulseGold {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(237, 190, 56, 0.6);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 8px rgba(237, 190, 56, 0);
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(237, 190, 56, 0);
  }
}

// Separated Premium Cards (Non-collapsible)
.premium-section-card {
  background: $jci-white !important;
  border-radius: 22px !important;
  border: 1px solid rgba(10, 15, 41, 0.06) !important;
  box-shadow: 0 8px 25px rgba(10, 15, 41, 0.02) !important;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px rgba(10, 15, 41, 0.05) !important;
  }
}

.card-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  z-index: 2;
}

.card-header-content {
  border-bottom: 1px solid rgba(10, 15, 41, 0.03);
}

.badge-num {
  font-family: "Outfit", sans-serif;
  font-size: 1.35rem;
  font-weight: 800;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-icon {
  background: #f4f6fc;
  border-radius: 12px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-title {
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  font-size: 1.35rem;
  margin-bottom: 2px;
}

.panel-subtitle {
  font-family: "Inter", sans-serif;
  font-size: 0.9rem;
  font-weight: 400;
}

// Progress dot
.section-progress-dot {
  width: 14px;
  height: 14px;
  background: #cbd5e1;
  border-radius: 50%;
  transition: all 0.4s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);

  &.completed {
    background: $jci-seaform;
    box-shadow: 0 0 10px rgba(86, 189, 163, 0.6);
  }
}

// Field Styling
.field-label {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 8px;
  display: block;
}

.text-danger {
  color: $jci-gold !important;
}

// Custom Deep styling Vuetify input slots
::v-deep .premium-input {
  .v-input__slot {
    background: $jci-white !important;
    border-radius: 12px !important;
    border: 1px solid rgba(10, 15, 41, 0.1) !important;
    box-shadow: inset 0 1px 3px rgba(10, 15, 41, 0.01) !important;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
    font-size: 1rem !important;

    fieldset {
      border: none !important;
    }

    &:hover {
      border-color: rgba(0, 151, 215, 0.4) !important;
      box-shadow: 0 4px 12px rgba(0, 151, 215, 0.06) !important;
    }
  }

  &.v-input--is-focused {
    .v-input__slot {
      border-color: $jci-navy !important;
      box-shadow: 0 0 0 3px rgba(58, 103, 177, 0.12) !important;
    }
  }

  .v-label {
    font-size: 0.95rem !important;
    color: #8892b0 !important;
  }

  .v-input__prepend-inner {
    margin-top: 10px !important;
    margin-right: 6px !important;
    color: #8892b0 !important;
    transition: color 0.3s ease;
  }

  &.v-input--is-focused .v-input__prepend-inner {
    color: $jci-navy !important;
  }
}

// Action Button Styling (Back to list)
.action-btn {
  font-family: "Outfit", sans-serif;
  text-transform: none;
  font-size: 1.05rem !important;
  letter-spacing: 0px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1px solid rgba(58, 103, 177, 0.15) !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(58, 103, 177, 0.1) !important;
  }
}

// Huge Submit Button
.submit-btn {
  font-family: "Outfit", sans-serif;
  text-transform: none;
  font-size: 1.25rem !important;
  letter-spacing: 0.5px;
  border-radius: 20px !important;
  background: linear-gradient(135deg, $jci-navy 0%, $jci-aqua 100%) !important;
  color: $jci-white !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  height: 64px !important;
  box-shadow: 0 12px 28px rgba(58, 103, 177, 0.25) !important;

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 151, 215, 0.3) !important;
  }

  &.v-btn--disabled {
    background: #cbd5e1 !important;
    color: #94a3b8 !important;
    box-shadow: none !important;
    transform: none !important;
  }
}

// Colors helper classes
.navy-text {
  color: $jci-navy !important;
}
.aqua-text {
  color: $jci-aqua !important;
}
.gold-text {
  color: $jci-gold !important;
}
.seaform-text {
  color: $jci-seaform !important;
}

.black-90-text {
  color: $jci-90-black !important;
}
.off-black-text {
  color: $jci-off-black !important;
}
.white-text {
  color: $jci-white !important;
}

.bg-navy {
  background-color: $jci-navy !important;
}
.bg-aqua {
  background-color: $jci-aqua !important;
}
.bg-gold {
  background-color: $jci-gold !important;
}
.bg-seaform {
  background-color: $jci-seaform !important;
}

.bg-navy-light {
  background-color: rgba(58, 103, 177, 0.08) !important;
}
.bg-aqua-light {
  background-color: rgba(0, 151, 215, 0.08) !important;
}
.bg-gold-light {
  background-color: rgba(237, 190, 56, 0.08) !important;
}
.bg-seaform-light {
  background-color: rgba(86, 189, 163, 0.08) !important;
}

// CSS Animations
.animate-slide-down {
  animation: slideDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.animate-fade-in {
  animation: fadeIn 1s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.animate-zoom-in {
  animation: zoomIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.animate-pulse-slow {
  animation: pulseSlow 4s infinite alternate ease-in-out;
}

.animate-pulse {
  animation: pulseGlow 2.5s infinite;
}

.delay-1 {
  animation-delay: 0.1s;
}
.delay-2 {
  animation-delay: 0.2s;
}
.delay-3 {
  animation-delay: 0.3s;
}
.delay-4 {
  animation-delay: 0.4s;
}

// Staggered loading for sections
.animate-staggered {
  .premium-section-card:nth-child(1) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
  }
  .premium-section-card:nth-child(2) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  }
  .premium-section-card:nth-child(3) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both;
  }
  .premium-section-card:nth-child(4) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
  }
  .premium-section-card:nth-child(5) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both;
  }
  .premium-section-card:nth-child(6) {
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulseSlow {
  0%,
  100% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.25);
  }
}

@keyframes pulseGlow {
  0% {
    box-shadow: 0 12px 28px rgba(58, 103, 177, 0.25);
  }
  50% {
    box-shadow: 0 12px 35px rgba(0, 151, 215, 0.4);
  }
  100% {
    box-shadow: 0 12px 28px rgba(58, 103, 177, 0.25);
  }
}

// Responsive Design
@media (max-width: 960px) {
  .page-container {
    padding: 1.5rem;
  }

  .header-title {
    font-size: 2.1rem;
  }

  .header-subtitle {
    font-size: 1rem;
  }

  .summary-box {
    padding: 12px;
  }
}

@media (max-width: 600px) {
  .page-container {
    padding: 1rem;
  }

  .header-content {
    padding: 20px !important;
  }

  .header-title {
    font-size: 1.6rem;
  }

  .header-subtitle {
    font-size: 0.9rem;
  }

  .badge-num {
    width: 36px;
    height: 36px;
    font-size: 1.15rem;
  }

  .panel-title {
    font-size: 1.15rem;
  }

  .submit-btn {
    height: 56px !important;
    font-size: 1.15rem !important;
  }
}
</style>
